import { SettingModelSettingType } from 'api/models';
import { emitInputAction } from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputCategory } from 'app/modules/wallforming/components/input-binding/types/inputCategory';
import { InputCommand } from 'app/modules/wallforming/components/input-binding/types/inputCommand';
import {
    cancel,
    isDraggingActive,
    isValidPlacementType,
    navigatePoi,
    navigateSettingList,
    paste,
    startTemporaryPoiNavigation,
    switchPoiType,
    unlessDragging
} from 'app/modules/wallforming/components/input-binding/utils/inputCommandUtils';
import { ProjectsDialog } from 'app/modules/wallforming/components/projects-manager/types/projectDialog';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import {
    dispatchPanelQuickReplacementDirection,
    type PanelReplacementDirection
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/quickPanelReplacementController';
import {
    canOpenPanelQuickReplacement,
    isPanelQuickReplacementInwardModifierActive
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';

import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';

/** Command pattern: one executable behavior per action; gestures are handled by their owning systems instead. */
export const inputCommands: Partial<Record<InputActionId, InputCommand>> = {
    [InputActionId.GeneralOpenKeyBindingPanel]: (event, state) => {
        event.stopPropagation();
        state.setIsInputBindingPanelOpen(true);
        state.setSelectedInputCategory(InputCategory.General);
    },
    [InputActionId.GeneralOpenProjects]: (_, state) => {
        state.setActiveProjectDialog(ProjectsDialog.NONE);
        state.setIsProjectPanelOpen(true);
    },
    [InputActionId.GeneralCancel]: (_, state) => cancel(state),
    [InputActionId.GeneralSave]: (event) => emitInputAction(InputActionId.GeneralSave, event),
    [InputActionId.GeneralToggleViewMode]: (_, state) => state.setIsViewMode(!state.isViewMode),
    [InputActionId.GeneralToggleCopilot]: (event) => {
        if (!(event instanceof KeyboardEvent) || event.repeat) {
            return false;
        }
        event.stopPropagation();
        emitInputAction(InputActionId.GeneralToggleCopilot, event);
    },

    [InputActionId.CameraCenter]: (event, state) => {
        if (event.target instanceof Element && event.target.closest('[data-input-scene]')) {
            emitInputAction(InputActionId.CameraCenter, event);
            return;
        }
        if (!state.mainCameraControlsRef.current) {
            return false;
        }
        state.setFocusOnBuilding(state.mainCameraControlsRef, state.mainWebGLRendererRef.current?.domElement ?? null);
    },
    [InputActionId.CameraLockToggle]: (_, state) => state.handleLockCameraControls(true),
    [InputActionId.CameraUnlockToggle]: (_, state) => state.handleLockCameraControls(false),

    [InputActionId.PoiNavigationModeToggle]: (_, state) =>
        state.setInteractionMode(
            state.interactionMode === InteractionMode.POI_NAVIGATION
                ? InteractionMode.DEFAULT
                : InteractionMode.POI_NAVIGATION
        ),
    [InputActionId.PoiNavigationTemporary]: startTemporaryPoiNavigation,
    [InputActionId.PoiPrevious]: navigatePoi(-1),
    [InputActionId.PoiNext]: navigatePoi(1),
    [InputActionId.PoiSwitchType]: switchPoiType(false),
    [InputActionId.PoiSwitchTypeReversed]: switchPoiType(true),
    [InputActionId.PoiToggleCameraRealignment]: (event, state) => {
        if (state.poiSelection.current.filterType === undefined) {
            return false;
        }
        event.stopPropagation();
        const source = PoiNavigationSource.PoiStepper;
        state.setDisableCameraAlign(source, !state.disableCameraAlignBySource[source]);
    },

    [InputActionId.ArticleCopy]: unlessDragging((state) => state.copyArticleInstances()),
    [InputActionId.ArticleCut]: unlessDragging((state) => state.cutArticleInstances()),
    [InputActionId.ArticlePaste]: unlessDragging(paste),
    [InputActionId.ArticleSelectAll]: unlessDragging((state) => state.selectAllArticleInstances()),
    [InputActionId.ArticleDelete]: (_, state) => state.discardArticleInstanceSelection(true),
    [InputActionId.ArticleTransform]: (event, state) => {
        const isTransformationInput =
            event.target instanceof HTMLElement && event.target.closest('[data-transformation-input]');
        if (!isTransformationInput && (isInputElement(event.target) || isDraggingActive(state))) {
            return false;
        }
        state.updatePivotControls();
    },
    [InputActionId.ArticleRotate]: (_, state) => state.handleOnArticleDefaultRotation(),
    [InputActionId.ArticleQuickReplacement]: (event, state) => {
        if (event instanceof KeyboardEvent && event.repeat) {
            return true;
        }
        if (state.isPanelQuickReplacementOpen) {
            state.setIsPanelQuickReplacementOpen(false);
            return;
        }
        if (!canOpenPanelQuickReplacement(state)) {
            return false;
        }
        state.setIsPanelQuickReplacementOpen(true);
    },
    [InputActionId.ArticleQuickReplacementLeft]: (event, state) =>
        dispatchQuickReplacementDirection(event, state, 'left'),
    [InputActionId.ArticleQuickReplacementRight]: (event, state) =>
        dispatchQuickReplacementDirection(event, state, 'right'),
    [InputActionId.ArticleQuickReplacementUp]: (event, state) =>
        dispatchQuickReplacementDirection(event, state, 'up'),
    [InputActionId.ArticleQuickReplacementDown]: (event, state) =>
        dispatchQuickReplacementDirection(event, state, 'down'),
    [InputActionId.ArticleGroup]: unlessDragging((state) => state.setIsGroupPanelOpen(true)),
    [InputActionId.ArticleUngroup]: unlessDragging((state) => state.handleOnUngroupSelection()),
    [InputActionId.ArticleShowContextMenu]: (_, state) => state.handleOnArticleContextMenu(),

    [InputActionId.PlacementExecute]: (event, state) => {
        if (
            !state.isPlacementDataInitialized ||
            !isValidPlacementType(
                state,
                SettingModelSettingType.Panels,
                SettingModelSettingType.Connectors,
                SettingModelSettingType.Anchors
            )
        ) {
            return false;
        }
        emitInputAction(InputActionId.PlacementExecute, event);
    },
    [InputActionId.PlacementApplySelectedSolution]: (event, state) => {
        if (!isValidPlacementType(state, SettingModelSettingType.Corners)) {
            return false;
        }
        emitInputAction(InputActionId.PlacementApplySelectedSolution, event);
    },
    [InputActionId.PlacementListNavigationUp]: navigateSettingList(-1),
    [InputActionId.PlacementListNavigationDown]: navigateSettingList(1)
};

function dispatchQuickReplacementDirection(
    event: Event,
    state: PlacementState,
    direction: PanelReplacementDirection
): boolean {
    if (!(event instanceof KeyboardEvent) || !state.isPanelQuickReplacementOpen) {
        return false;
    }
    if (event.repeat) {
        return true;
    }
    dispatchPanelQuickReplacementDirection(direction, isPanelQuickReplacementInwardModifierActive(state, event));
    return true;
}
